import { authContext } from '@staffedupusa/shared-components';
import { useContext, useState } from 'react';

import PickImageModal from '@components/PickImageModal/PickImageModal';
import BoostButtonElement from '@features/boostMyJobs/components/BoostButtonElement/BoostButtonElement';
import BoostModal from '@features/boostMyJobs/components/BoostModal/BoostModal';
import NeedCustomHiringGraphicModal from '@features/boostMyJobs/components/CustomHiringGraphicModal/NeedCustomHiringGraphicModal';
import GraphicRequestedModal from '@features/boostMyJobs/components/GraphicRequestedModal/GraphicRequestedModal';
import LocationBoostModal from '@features/boostMyJobs/components/LocationBoostModal/LocationBoostModal';
import PostHiringLinkModal from '@features/boostMyJobs/components/PostHiringLinkModal/PostHiringLinkModal';
import SocialBoostModal from '@features/boostMyJobs/components/SocialBoostModal/SocialBoostModal';
import { useUpdateProfileImage } from '@features/profile/hooks/useUpdateProfileImage';
import CompaniesDropdownModal from '../CompaniesDropdownModal/CompaniesDropdownModal';
import { useGetMetrics } from '@features/enterpriseMetrics/hooks/useGetSavedMessages';
import { useUpdateSocialImage } from '@features/profile/hooks/useUpdateSocialImage';
import { track } from '@amplitude/analytics-browser';
import { amplitudeEventsName } from '@utils/amplitudeEvents';
import { selectedPlanName } from '@utils/pricing';

const BoostButton = () => {
  const { authData } = useContext(authContext);
  const [errorText, setErrorText] = useState('');
  const [isBoostOpen, setIsBoostOpen] = useState(false);
  const [isSocialBoostOpen, setIsSocialBoostOpen] = useState(false);
  const [isLocationBoostOpen, setIsLocationBoostOpen] = useState(false);
  const [isPostLinkModalOpen, setIsPostLinkModalOpen] = useState(false);
  const [isGraphicModalOpen, setIsGraphicModalOpen] = useState(false);
  const [isGraphicRequestedModalOpen, setIsGraphicRequestedModalOpen] =
    useState(false);

  const [selectedCompanyId, setSelectedCompanyId] = useState<string | number>(
    '',
  );
  const [isCompanyPaid, setIsCompanyPaid] = useState<boolean>(false);
  const [showCompanyModal, setShowCompanyModal] = useState(false);

  const [isSelectImageModalOpen, setIsSelectImageModalOpen] = useState(false);
  const isCompany = authData.user?.company;
  const selectedPlan = selectedPlanName(authData?.user?.company?.plan || 0);

  const { updateProfileImage, isUpdatingProfileImage } = useUpdateProfileImage({
    setAlertText: setErrorText,
  });

  const { updateSocialImage, isUpdatingSocialImage } = useUpdateSocialImage({
    setAlertText: setErrorText,
  });

  const { metricsData, isLoadingMetrics } = useGetMetrics({
    enterprise_id: authData?.user?.enterprise?.id,
    selectedCompanyId: 'ALL',
  });

  const closeBoostModal = () => {
    setIsBoostOpen(false);
  };

  const showSocialBoostModal = () => {
    isCompany &&
      track(
        `${amplitudeEventsName.APPLICANTS_CLICK_REQUEST_SOCIAL_BOOST}-${selectedPlan}`,
      );
    closeBoostModal();
    setIsSocialBoostOpen(true);
  };

  const showLocationBoostModal = () => {
    isCompany &&
      track(
        `${amplitudeEventsName.APPLICANTS_CLICK_REQUEST_LOCATION_BOOST}-${selectedPlan}`,
      );
    closeBoostModal();
    setIsLocationBoostOpen(true);
  };

  const showPostHiringLinkModal = () => {
    isCompany &&
      track(
        `${amplitudeEventsName.APPLICANTS_CLICK_POST_JOB_SOCIAL}-${selectedPlan}`,
      );
    const hasCompanySocialImage = authData.user?.enterprise
      ? metricsData?.companies?.find(
          (item) => item.company_id == selectedCompanyId,
        )?.has_social_image
      : authData.user?.has_social_image;

    closeBoostModal();
    if (hasCompanySocialImage) {
      setIsPostLinkModalOpen(true);
    } else {
      setIsGraphicModalOpen(true);
    }
  };

  return (
    <>
      <BoostButtonElement
        onClick={() => {
          if (authData?.user?.enterprise) {
            setShowCompanyModal(true);
          } else {
            track(
              `${amplitudeEventsName.APPLICANTS_CLICK_BOOST_MY_JOBS}-${selectedPlan}`,
            );
            setIsBoostOpen(!isBoostOpen);
          }
        }}
      />

      <BoostModal
        isOpen={isBoostOpen}
        handleClose={closeBoostModal}
        handleRequestLocationClick={showLocationBoostModal}
        handleRequestSocialClick={showSocialBoostModal}
        handlePostToSocialMedia={showPostHiringLinkModal}
        selectedCompanyId={selectedCompanyId}
      />
      {isSocialBoostOpen && (
        <SocialBoostModal
          isOpen={isSocialBoostOpen}
          selectedCompanyId={selectedCompanyId}
          companySelfPaid={isCompanyPaid}
          handleClose={() => {
            setIsSocialBoostOpen(false);
            setShowCompanyModal(false);
            setIsCompanyPaid(false);
            setSelectedCompanyId('');
          }}
        />
      )}
      {isLocationBoostOpen && (
        <LocationBoostModal
          isOpen={isLocationBoostOpen}
          selectedCompanyId={selectedCompanyId}
          companySelfPaid={isCompanyPaid}
          handleClose={() => {
            setShowCompanyModal(false);
            setIsLocationBoostOpen(false);
            setIsCompanyPaid(false);
            setSelectedCompanyId('');
          }}
        />
      )}
      <PostHiringLinkModal
        isOpen={isPostLinkModalOpen}
        selectedCompanyId={selectedCompanyId}
        handleClose={() => {
          setIsPostLinkModalOpen(false);
          setShowCompanyModal(false);
          setIsCompanyPaid(false);
          setSelectedCompanyId('');
        }}
      />
      <NeedCustomHiringGraphicModal
        isOpen={isGraphicModalOpen}
        selectedCompanyId={selectedCompanyId}
        handleClose={() => setIsGraphicModalOpen(false)}
        handleRequestGraphicSuccess={() => {
          setIsGraphicModalOpen(false);
          setIsGraphicRequestedModalOpen(true);
        }}
        handleUploadImage={() => {
          setIsGraphicModalOpen(false);
          setIsSelectImageModalOpen(true);
        }}
        handleContinueWithout={() => {
          setIsGraphicModalOpen(false);
          setIsPostLinkModalOpen(true);
        }}
      />
      <GraphicRequestedModal
        isOpen={isGraphicRequestedModalOpen}
        handleClose={() => setIsGraphicRequestedModalOpen(false)}
      />
      <CompaniesDropdownModal
        handleClose={() => {
          setShowCompanyModal(false);
          setIsCompanyPaid(false);
          setSelectedCompanyId('');
        }}
        isOpen={showCompanyModal}
        setSelectedCompanyId={setSelectedCompanyId}
        setIsCompanyPaid={setIsCompanyPaid}
        selectedCompanyId={selectedCompanyId}
        setIsBoostOpen={setIsBoostOpen}
      />
      <PickImageModal
        isOpen={isSelectImageModalOpen}
        setIsOpen={setIsSelectImageModalOpen}
        title="Upload Image"
        imageType="social_image"
        minWidth={0}
        minHeight={0}
        alertText={errorText}
        setAlertText={setErrorText}
        selectedCompanyId={selectedCompanyId}
        handleUpdateImage={
          authData.user?.enterprise ? updateSocialImage : updateProfileImage
        }
        isUpdatingImage={
          authData.user?.enterprise
            ? isUpdatingSocialImage
            : isUpdatingProfileImage
        }
      />
    </>
  );
};

export default BoostButton;
